import React, { useEffect, useState } from "react";
import { TBlockContainerStateProps } from "../../state/TBlockContainerStateProps";
import { formOperation } from "../../wrappers/formOperation";
import { CaseCasePifReadForm, TCaseCasePifReadForm } from "../../../../domain/codecs/form/ClientCaseForm";
import { UuidForm } from "../../../../domain/codecs/form/UuidForm";
import { TCasePifFreeText, TCasePifRiskFlag } from "../../../../domain/codecs/CasePif";
import { pifRiskRulesPresentation } from "../../../../domain/pif/pifRiskRulesPresentation";
import { pifSections, shouldShowField, shouldShowSection, fieldAllowsDontKnow, pifNotKnownText } from "../../../../domain/pif/pifSchema";
import { CRMBlockScrollCacheView } from "../../components/CRM/CRMBlock/CRMBlockViews/CRMBlockViews";
import { BackgroundColour } from "../../components/BuildingBlocks/BackgroundColour";
import { MinHeight } from "../../components/BuildingBlocks/MinHeight";
import { Padding } from "../../components/BuildingBlocks/Padding";
import { Sticky } from "../../components/BuildingBlocks/Sticky";
import { CRMSingleViewHeader } from "../../components/Simple/CRMSingleViewHeader/CRMSingleViewHeader";
import { CRMSpacer } from "../../components/CRMSpacer/CRMSpacer";
import { CRMTitleSection } from "../../components/CRMTitleSection/CRMTitleSection";
import { CRMParagraph } from "../../components/Simple/CRMParagraph/CRMParagraph";
import { SpacingRow } from "../../components/BuildingBlocks/SpacingRow";
import { SpacingColumn } from "../../components/BuildingBlocks/SpacingColumn";
import { CRMSpacing } from "../../models/CRMSpacing";
import { WeightBold } from "../../components/WeightBold/WeightBold";
import { WeightSemiBold } from "../../components/WeightSemiBold/WeightSemiBold";
import { CRMStatusPill } from "../../components/Simple/CRMStatusPill/CRMStatusPill";
import { CRMColors } from "../../models/CRMColors";
import { CRMButtonPrimary } from "../../components/CRMButtonPrimary/CRMButtonPrimary";
import { CRMIcon } from "../../components/CRMIcon/CRMIcon";
import { DateTime } from "luxon";

const CRMLegalCaseCasePifContainer = (props: TBlockContainerStateProps): JSX.Element => {
    const caseId: string = props.state.forms.case_details_page.data.output.details.children.id;

    const [pif, setPif] = useState<TCaseCasePifReadForm | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isReopening, setIsReopening] = useState(false);

    const load = async () => {
        setIsLoading(true);
        const result = await formOperation("GetCaseCasePif", {
            ...CaseCasePifReadForm.newDefault(),
            edited: { case_id: caseId },
            original: { case_id: caseId },
        });
        setPif(result);
        setIsLoading(false);
    };

    useEffect(() => {
        load();
    }, [caseId]);

    const reopen = async () => {
        if (!window.confirm("Reopen this PIF? The seller's answers will be preserved; they'll be able to edit and resubmit.")) return;
        setIsReopening(true);
        await formOperation("ReopenClientCaseCasePif", {
            ...UuidForm.newDefault(),
            edited: { id: caseId },
            original: { id: caseId },
        });
        setIsReopening(false);
        await load();
    };

    if (isLoading || !pif) {
        return (
            <CRMBlockScrollCacheView isShowing={true}>
                <BackgroundColour colour={CRMColors.NEUTRAL_PAPER}>
                    <MinHeight height="calc(100vh - 216px)">
                        <Padding spacing={CRMSpacing.MEDIUM}>
                            <CRMParagraph>Loading…</CRMParagraph>
                        </Padding>
                    </MinHeight>
                </BackgroundColour>
            </CRMBlockScrollCacheView>
        );
    }

    const lastEdited = pif.children.last_edited_at
        ? DateTime.fromISO(pif.children.last_edited_at).toRelative()
        : null;

    return (
        <CRMBlockScrollCacheView isShowing={true}>
            {/* STICKY HEADER */}
            <Sticky zIndex={4}>
                <CRMSingleViewHeader title="Property Information Form">
                    <BackgroundColour colour={CRMColors.NEUTRAL_PAPER}>
                        <Padding spacing={`0px ${CRMSpacing.MEDIUM}`}>
                            <SpacingRow spacing={CRMSpacing.SMALL} alignItems="center">
                                {pif.children.is_submitted ? (
                                    <CRMStatusPill
                                        pillColor={CRMColors.HIGHLIGHTS_POSITIVE_GREEN_12}
                                        borderColor={CRMColors.HIGHLIGHTS_POSITIVE_GREEN_2}
                                        textColor={CRMColors.NEUTRAL_INK}
                                        label="Submitted"
                                    />
                                ) : (
                                    <CRMStatusPill
                                        pillColor={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_10}
                                        borderColor={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_2}
                                        textColor={CRMColors.NEUTRAL_INK}
                                        label={lastEdited ? `Draft — ${lastEdited}` : "Not yet started"}
                                    />
                                )}
                                {pif.children.is_submitted && (
                                    <CRMButtonPrimary
                                        label={isReopening ? "Reopening…" : "Reopen for seller"}
                                        onClick={reopen}
                                        disabled={isReopening}
                                    />
                                )}
                            </SpacingRow>
                        </Padding>
                    </BackgroundColour>
                </CRMSingleViewHeader>
            </Sticky>

            <BackgroundColour colour={CRMColors.NEUTRAL_PAPER}>
                <MinHeight height="calc(100vh - 216px)">
                    <Padding spacing={CRMSpacing.MEDIUM}>
                        <PifBlockBody
                            data={pif.children.data}
                            freeText={pif.children.free_text}
                            riskFlags={pif.children.risk_flags}
                            isSubmitted={pif.children.is_submitted}
                            includeLeasehold={pif.children.include_leasehold_section}
                            titleMode={pif.children.title_mode}
                            autoAnsweredKeys={pif.children.auto_answered_keys}
                            titleIndicatedOwnershipType={pif.children.title_indicated_ownership_type}
                        />
                    </Padding>
                </MinHeight>
            </BackgroundColour>
        </CRMBlockScrollCacheView>
    );
};

// Exported so the "seller didn't know" list and its from-the-title hint can be
// rendered and asserted on without standing up the whole CRM state tree.
export const PifBlockBody = (props: {
    data: TCaseCasePifReadForm["children"]["data"],
    freeText: TCasePifFreeText[],
    riskFlags: TCasePifRiskFlag[],
    isSubmitted: boolean,
    includeLeasehold: boolean,
    titleMode: TCaseCasePifReadForm["children"]["title_mode"],
    autoAnsweredKeys: string[],
    titleIndicatedOwnershipType: TCaseCasePifReadForm["children"]["title_indicated_ownership_type"],
}): JSX.Element => {
    const redFlags = props.riskFlags.filter((f) => f.severity === "red");
    const amberFlags = props.riskFlags.filter((f) => f.severity === "amber");
    const freeTextByKey: Record<string, string> = Object.fromEntries(
        props.freeText.map((ft) => [ft.field_key, ft.value]),
    );
    const autoAnsweredSet = new Set(props.autoAnsweredKeys);
    const sellerDidntKnow = pifSections
        .filter((section) => shouldShowSection(section, props.includeLeasehold))
        .flatMap((section) => section.fields
            .filter((field) => fieldAllowsDontKnow(field)
                && autoAnsweredSet.has(field.key)
                && shouldShowField(field, props.data))
            .map((field) => ({
                sectionTitle: section.title,
                label: field.label,
                titleSays: field.key === "ownership_type" && props.titleIndicatedOwnershipType === "tenants_in_common"
                    ? "the title carries a Form A restriction, so tenants in common"
                    : null,
            })));

    return (
        <SpacingColumn spacing={CRMSpacing.LARGE}>
            {props.titleMode === "limited" && (
                <CRMParagraph>
                    <WeightSemiBold>Limited title — sold by a personal representative.</WeightSemiBold> Answers they couldn't give from personal knowledge show as "Not known — PR" below.
                </CRMParagraph>
            )}

            {sellerDidntKnow.length > 0 && (
                <SpacingColumn spacing={CRMSpacing.SMALL}>
                    <CRMParagraph>
                        <WeightSemiBold>The seller didn't know the answer to {sellerDidntKnow.length} {sellerDidntKnow.length === 1 ? "question" : "questions"}.</WeightSemiBold> Look these up in the title or the management pack — don't go back to the client.
                    </CRMParagraph>
                    {sellerDidntKnow.map((q) => (
                        <CRMParagraph key={q.label}>
                            {q.sectionTitle} — {q.label}
                            {q.titleSays !== null && <> · <i>From the title: {q.titleSays}</i></>}
                        </CRMParagraph>
                    ))}
                </SpacingColumn>
            )}

            {/* RISK FLAGS */}
            {(redFlags.length > 0 || amberFlags.length > 0) && (
                <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                    {redFlags.map((f) => (
                        <RiskFlagCard key={f.rule_code} flag={f} severity="red" />
                    ))}
                    {amberFlags.map((f) => (
                        <RiskFlagCard key={f.rule_code} flag={f} severity="amber" />
                    ))}
                </SpacingColumn>
            )}

            {redFlags.length === 0 && amberFlags.length === 0 && props.isSubmitted && (
                <CRMParagraph>
                    <WeightSemiBold>No risk flags raised on submission.</WeightSemiBold>
                </CRMParagraph>
            )}

            {/* ANSWERS BY SECTION */}
            <CRMTitleSection>Answers</CRMTitleSection>
            <CRMSpacer size="medium" />
            <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                {pifSections
                    .filter((section) => shouldShowSection(section, props.includeLeasehold))
                    .map((section) => (
                        <SpacingColumn key={section.key} spacing={CRMSpacing.SMALL}>
                            <CRMParagraph><WeightBold>{section.title}</WeightBold></CRMParagraph>
                            {section.fields
                                .filter((field) => shouldShowField(field, props.data))
                                .map((field) => (
                                    <SpacingRow key={field.key} spacing={CRMSpacing.MEDIUM} alignItems="flex-start">
                                        <div className="crm-case-pif-answer-row__label">
                                            <CRMParagraph><WeightSemiBold>{field.label}</WeightSemiBold></CRMParagraph>
                                        </div>
                                        <div className="crm-case-pif-answer-row__value">
                                            {autoAnsweredSet.has(field.key)
                                                ? <CRMParagraph><i>{pifNotKnownText(field, props.titleMode)}</i></CRMParagraph>
                                                : <CRMParagraph>{formatAnswer(field.type, props.data[field.key], field.options)}</CRMParagraph>}
                                            {(freeTextByKey[`${field.key}__details`] ?? freeTextByKey[field.key]) && (
                                                <CRMParagraph>
                                                    <i>Details: {freeTextByKey[`${field.key}__details`] ?? freeTextByKey[field.key]}</i>
                                                </CRMParagraph>
                                            )}
                                        </div>
                                    </SpacingRow>
                                ))}
                        </SpacingColumn>
                    ))}
            </SpacingColumn>
        </SpacingColumn>
    );
};

const RiskFlagCard = (props: {
    flag: TCasePifRiskFlag,
    severity: "red" | "amber",
}): JSX.Element => {
    const presentation = pifRiskRulesPresentation[props.flag.rule_code];
    if (!presentation) return <></>;
    return (
        <div className={`crm-case-pif-risk-flag-card crm-case-pif-risk-flag-card--${props.severity}`}>
            <SpacingRow spacing={CRMSpacing.MEDIUM} alignItems="flex-start">
                <div className="crm-case-pif-risk-flag-card__icon">
                    <CRMIcon
                        iconName="warning-bubble"
                        colour={props.severity === "red" ? "highlights-instructional-brick-0" : "highlights-pertinent-yellow-4"}
                    />
                </div>
                <SpacingColumn spacing={CRMSpacing.TINY}>
                    <CRMParagraph>
                        <WeightBold>{presentation.field_label}</WeightBold>
                    </CRMParagraph>
                    <CRMParagraph>{presentation.summary}</CRMParagraph>
                    <CRMParagraph>
                        <i>{presentation.advice}</i>
                    </CRMParagraph>
                    <CRMParagraph>
                        <WeightSemiBold>Source:</WeightSemiBold> {presentation.section}
                    </CRMParagraph>
                </SpacingColumn>
            </SpacingRow>
        </div>
    );
};

const formatAnswer = (type: string, value: unknown, options?: { value: string, label: string }[]): string => {
    if (value === null || value === undefined || value === "") return "—";
    if (type === "boolean") return value === true ? "Yes" : value === false ? "No" : String(value);
    if (type === "select" && options) {
        const opt = options.find((o) => o.value === value);
        if (opt) return typeof opt.label === "string" ? opt.label : String(value);
    }
    return String(value);
};

export default CRMLegalCaseCasePifContainer;
